import { useLayoutEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "store/useSelector";
import { useSettings } from "../../../../contexts/SettingsManager/SettingsManager";
import { useIsDarkMode } from "store/atoms";
import {
  darkGrey,
  medBlue,
  lightText,
  darkModeTextColor,
  darkModeCardBorderColor,
  darkModeModalBackgroundColor,
} from "../../../../constants/colors";
import { TILE_WIDTH_PX_SM } from "../../../../constants/constants";
import { responsiveFontSize } from "../ProcessTile/ProcessTileStyles";
import { usePrevious } from "../../../../constants/constants";
import { ONE_MINUTE_OF_MS } from "../../../../utils/timeUtils";
import { isEqual, cloneDeep, uniqBy } from "lodash";
import GroupTiles from "./GroupTiles";
import { GroupTilesPanelStyles } from "./GroupTilesPanelStyles";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useGetPageFromPath,
  useVisibleGroups,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import useWindowSize from "utils/customHooks/useWindowSize";
import useContainerDimensions from "utils/useContainerDimensions";
import { useHeightAboveTiles, getMaxSquareWidth } from "pages/useTileSize";
import { Process, ProcessGroup } from "types/types";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useCachedProcessGroups } from "../ProcessTile/processTileUtils";
import {
  useGroupTilesFilterState,
  useSetGroupTilesFilterStateFromUrlOnMount,
} from "./groupTilesUtils";
import { Button } from "@mui/material";
import styled from "styled-components/macro";
import { SubdirectoryArrowRight, Info } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useAtom } from "jotai";
import { allProcessGroupsAtom } from "components/DataGetter/ProcessGroupsGetter";
import TileLoaderSkeleton from "../TileLoaderSkeleton";

const GroupTilesPanel = () => {
  const ckey = useCustomerKey();
  const [isLoading, setIsLoading] = useState(true);

  // callStart = if applicable, what time did the call (for maintenance = hand symbol) start
  // isIdleUntagged = exclamation mark on GroupTile indicating "something is unknown, please go tag it"

  const [cachedProcessGroups] = useCachedProcessGroups();
  const latestProcessGroups = useProcessGroupsWithMidgardData();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const [{ searchText, selectedGroupNames }] = useGroupTilesFilterState();
  useSetGroupTilesFilterStateFromUrlOnMount();
  const processGroups = useMemo(() => {
    let processGroups = uniqBy(
      [
        ...latestProcessGroups,
        ...(cachedProcessGroups[ckey]?.[workScheduleId] ?? []),
      ],
      pg => pg.shortName
    )
      .sort((a, b) => Number(a.groupSortIdx) - Number(b.groupSortIdx))
      .filter(pg => {
        if (!isAllWorkSchedulesPage) {
          return true;
        }
        const matchesSearchText = searchText
          ? pg.longName.toLowerCase().includes(searchText.toLowerCase())
          : false;

        const matchesSelectedGroupNames =
          selectedGroupNames.length === 0 ||
          selectedGroupNames.includes(pg.longName) ||
          selectedGroupNames.includes("SELECT_ALL");
        return matchesSearchText || matchesSelectedGroupNames;
      });

    if (isAllWorkSchedulesPage) {
      processGroups = processGroups.filter(pg => {
        if (!isAllWorkSchedulesPage) {
          return true;
        }
        const matchesSearchText = searchText
          ? pg.longName.toLowerCase().includes(searchText.toLowerCase())
          : false;

        if (selectedGroupNames.length === 0) {
          return matchesSearchText || !searchText;
        }

        const matchesSelectedGroupNames =
          selectedGroupNames.includes(pg.longName) ||
          selectedGroupNames.includes("SELECT_ALL");

        return matchesSearchText || matchesSelectedGroupNames;
      });
    }
    return processGroups;
  }, [
    latestProcessGroups,
    cachedProcessGroups,
    ckey,
    workScheduleId,
    isAllWorkSchedulesPage,
    searchText,
    selectedGroupNames,
  ]);

  const { isLargeTiles } = useSettings();

  const [ref, dimensions] = useContainerDimensions();
  const panelWidth = dimensions?.width || 0;

  const heightAboveTiles = useHeightAboveTiles();

  const { innerHeight } = useWindowSize();
  const [
    {
      general: { tileWidthMultiplier: tileWidthConfig },
    },
  ] = useFlightConfig();

  const { tileWidth } = useMemo(
    () =>
      getMaxSquareWidth({
        numTiles: processGroups.length,
        panelHeight: innerHeight - heightAboveTiles,
        panelWidth,
      }),
    [processGroups.length, panelWidth, innerHeight, heightAboveTiles]
  );

  // remember last known counts to prevent flickering to "--" on data update
  const [lastKnownGroupTilesCounts, setLastKnownGroupTilesCounts] = useState(
    {} as any
  );

  const prevProcessGroup = usePrevious(processGroups);

  // track the last known outputs (counts) so the tiles don't flicker
  // between counts and performance % as we get new counts data
  useLayoutEffect(() => {
    if (isEqual(prevProcessGroup, processGroups)) {
      return;
    }

    // If we have process groups data (even if it's an empty array), loading has completed
    setIsLoading(false);
    const newlastKnownGroupTilesCounts = cloneDeep(lastKnownGroupTilesCounts);

    // initialize the memorized objects if they don't exist
    processGroups.forEach(({ groupSortIdx }) => {
      if (!newlastKnownGroupTilesCounts[groupSortIdx]) {
        newlastKnownGroupTilesCounts[groupSortIdx] = {
          value: null as number | null,
          label: null as string | null,
        };
      }
    });

    let shouldUpdate = false;

    processGroups.forEach(({ groupSortIdx, count, shortName, outputLabel }) => {
      const output = count;
      // if there's an output (even 0), and if it's different, memorize it
      if (
        (output || output === 0) &&
        newlastKnownGroupTilesCounts[groupSortIdx].value !== output &&
        // exception: for "All" tiles, preserve the largest count
        // (otherwise it flickers between individual & summed counts)
        (shortName !== "All" ||
          newlastKnownGroupTilesCounts[groupSortIdx].value < output)
      ) {
        newlastKnownGroupTilesCounts[groupSortIdx].value = output;
        shouldUpdate = true;
      }
      // if there's a label (e.g. "parts"), and if it's different, memorize it
      if (
        outputLabel &&
        newlastKnownGroupTilesCounts[groupSortIdx].label !== outputLabel
      ) {
        newlastKnownGroupTilesCounts[groupSortIdx].label = outputLabel;
        shouldUpdate = true;
      }
    });
    if (
      shouldUpdate &&
      !isEqual(newlastKnownGroupTilesCounts, lastKnownGroupTilesCounts)
    ) {
      setLastKnownGroupTilesCounts(newlastKnownGroupTilesCounts);
    }
  }, [processGroups, lastKnownGroupTilesCounts, prevProcessGroup]);

  const { shouldRedirect, redirectToUrl } =
    useShouldRedirectIntoFirstGroup(workScheduleId);
  const [allProcessGroups] = useAtom(allProcessGroupsAtom);

  if (shouldRedirect) {
    return <Navigate replace={true} to={redirectToUrl} />;
  }

  return (
    <div
      ref={ref}
      className="tilesWrapper"
      data-server-names={[SERVER_NAMES.midgardProcessGroups]}
    >
      <GroupTilesPanelStyles
        data-component-name={"Group Tiles Panel"}
        $isLargeTiles={isLargeTiles}
        $tileWidth={tileWidth}
        $tileWidthConfig={tileWidthConfig}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <TileLoaderSkeleton
              tileWidth={tileWidth}
              isLargeTiles={isLargeTiles}
            />
          </div>
        ) : allProcessGroups.length > 0 && processGroups.length === 0 ? (
          <EmptyWorkScheduleMessage />
        ) : (
          <GroupTiles
            processGroups={processGroups}
            tileWidth={tileWidth}
            lastKnownGroupTilesCounts={lastKnownGroupTilesCounts}
            customerKey={ckey}
          />
        )}
      </GroupTilesPanelStyles>
      <ViewSelectedGroupsButton visibleProcessGroups={processGroups} />
    </div>
  );
};

export default GroupTilesPanel;

type ProcessGroupWithMachemetryData = ProcessGroup & {
  count: number | null;
  callStart: number | null;
  isIdleUntagged: boolean;
};

export function useShouldRedirectIntoFirstGroup(workScheduleId: string) {
  const ckey = useCustomerKey();
  const [
    {
      general: { autoRedirectIntoFirstGroup },
    },
  ] = useFlightConfig();
  const { isGroupTilesPage } = useGetPageFromPath();

  const dontAutoRedirectCustomFlag =
    window.localStorage.getItem(
      "internal-flag:flightDontAutoRedirectIntoGroupTile"
    ) === "true";

  const visibleGroups = useVisibleGroups();
  const firstProcessGroup = visibleGroups.filter(
    g => g.processes[0]?.workScheduleConfig?.id === workScheduleId
  )[0];

  const [cachedProcessGroups] = useCachedProcessGroups();
  const cachedProcessGroupsWorkSchedule =
    cachedProcessGroups[ckey]?.[workScheduleId] ?? {};
  const firstCachedProcessGroup = cachedProcessGroupsWorkSchedule[0];
  const shouldRedirect =
    ckey &&
    (!isGroupTilesPage ||
      (firstProcessGroup && ckey === firstProcessGroup.ckey)) &&
    autoRedirectIntoFirstGroup &&
    !dontAutoRedirectCustomFlag; // don't redirect if we've set flightDontAutoRedirectIntoGroupTile via FlightDebugger > FlagsEditor, e.g. if you want to use the "quickView=lastWeek" internal button on GroupTile (so you always want access to the GroupTile)

  const summaryGroup =
    (firstProcessGroup ?? firstCachedProcessGroup)?.summaryGroup ?? "";

  // redirect into the group for specified customers only
  const redirectToUrl = `/${ckey}/dashboard${
    summaryGroup ? `/${summaryGroup}` : ""
  }?workSchedule=${workScheduleId}`;
  return { shouldRedirect, redirectToUrl };
}

// tslint:disable-next-line: cognitive-complexity
export function useProcessGroupsWithMidgardData(): ProcessGroupWithMachemetryData[] {
  const segments = useSelector(state => state.legacy.segments);
  const groupTilesDDAs = useSelector(state => state.legacy.dataDumpsGroupTiles);
  const visibleGroups = useVisibleGroups();
  const [flightConfig] = useFlightConfig();
  return useMemo(() => {
    const now = Date.now();
    return visibleGroups
      .sort(
        // sort by groupSortIdx (goes to end if undefined)
        (a, b) => Number(a.groupSortIdx) - Number(b.groupSortIdx)
      )
      .map(group => {
        // sum together the group's processes to get count, callStart, isIdleuntagged, outputLabel
        const { count, callStart, isIdleUntagged } = (group.processes ?? [])
          // roll up the count/callStart/isIdleUntagged/outputLabel
          .reduce(
            (acc, process: Process, idx) => {
              const dataDump = groupTilesDDAs[process.id];

              // outputLabel comes from flightConfig
              // group label will match the first process label in the group
              if (idx === 0) {
                if (
                  flightConfig.hubletView.bottomRowItemsByProcess?.[process.id]
                    ?.item1?.displayName
                ) {
                  acc.outputLabel = String(
                    flightConfig.hubletView.bottomRowItemsByProcess?.[
                      process.id
                    ]?.item1?.displayName
                  );
                } else if (
                  flightConfig.hubletView.bottomRowItems?.item1?.displayName
                ) {
                  acc.outputLabel = String(
                    flightConfig.hubletView.bottomRowItems?.item1?.displayName
                  );
                }
              }

              // isIdleUntagged: is the last segment pnl.category === 'unknown' for > 5 min
              const lastSegment = segments[process.id]
                ? segments[process.id].slice(-1)[0]
                : null;

              // if any process is "unknown", flag it as "idle/untagged" (exclamation mark on group tile)
              const shouldFlagIdleUntagged =
                lastSegment &&
                lastSegment.pnl &&
                lastSegment.pnl.category === "unknown" &&
                now - Number(lastSegment.range.ge) > 5 * ONE_MINUTE_OF_MS;
              if (shouldFlagIdleUntagged) {
                acc.isIdleUntagged = true;
              }

              // return the oldest callStart
              if (process.callStart) {
                acc.callStart = acc.callStart
                  ? Math.min(acc.callStart, process.callStart)
                  : process.callStart;
              }
              // sum the counts
              const processCount = dataDump?.netOutput;
              if (processCount) {
                acc.count = (acc.count || 0) + processCount;
              }

              return acc;
            },
            {
              count: null as number | null,
              callStart: null as number | null,
              isIdleUntagged: false,
              outputLabel: null as string | null,
            }
          );
        return { ...group, count, callStart, isIdleUntagged };
      });
  }, [groupTilesDDAs, segments, visibleGroups, flightConfig]);
}

function ViewSelectedGroupsButton({ visibleProcessGroups }) {
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const [, setGroupTilesFilterState] = useGroupTilesFilterState();
  if (!isAllWorkSchedulesPage) {
    return null;
  }
  const processGroupsNames =
    visibleProcessGroups.length === 0
      ? allGroups.map(g => g.longName)
      : visibleProcessGroups.map(g => g.longName);

  const selectedGroupsUrl = `ALL?workSchedule=all&groups=${encodeURIComponent(
    processGroupsNames.join(",")
  )}`;
  return (
    <ViewSelectedGroupsButtonStyles className="viewSelectedGroupsButton">
      <Link to={selectedGroupsUrl}>
        <Button
          variant={"contained"}
          startIcon={<SubdirectoryArrowRight />}
          onClick={() => {
            // ensure the matching processGroups are added to the filter state before moving to the next page
            setGroupTilesFilterState(prev => ({
              ...prev,
              selectedGroupNames: processGroupsNames,
              searchText: null,
            }));
          }}
        >
          View These Groups
        </Button>
      </Link>
    </ViewSelectedGroupsButtonStyles>
  );
}
const ViewSelectedGroupsButtonStyles = styled.div`
  margin: 0.5em auto 2em;
  && {
    button {
      color: white;
    }
  }
`;

function EmptyWorkScheduleMessage() {
  const { isLargeTiles } = useSettings();
  const [isDarkMode] = useIsDarkMode();
  const { innerHeight } = useWindowSize();
  const heightAboveTiles = useHeightAboveTiles();
  const [ref, dimensions] = useContainerDimensions();
  const panelWidth = dimensions?.width || 0;

  // Get the same tile width calculation as the GroupTilesPanel
  const { tileWidth } = useMemo(
    () =>
      getMaxSquareWidth({
        numTiles: 1, // Single tile
        panelHeight: innerHeight - heightAboveTiles,
        panelWidth,
      }),
    [panelWidth, innerHeight, heightAboveTiles]
  );

  return (
    <div
      ref={ref}
      style={{ display: "flex", justifyContent: "center", padding: "20px" }}
    >
      <EmptyTileStyles
        $isDarkMode={isDarkMode}
        $isLargeTiles={isLargeTiles}
        $tileWidth={tileWidth}
      >
        <div className="empty-tile">
          <div className="tile-content">
            <Info className="info-icon" />
            <div className="message-title">No Groups Found</div>
            <div className="message-body">
              No process groups configured for this work schedule
            </div>
          </div>
        </div>
      </EmptyTileStyles>
    </div>
  );
}

const EmptyTileStyles = styled.div<{
  $isDarkMode: boolean;
  $isLargeTiles: boolean;
  $tileWidth?: number;
}>`
  .empty-tile {
    border: 2px dashed
      ${({ $isDarkMode }) => ($isDarkMode ? darkModeCardBorderColor : medBlue)};
    height: ${props =>
      props.$isLargeTiles && props.$tileWidth
        ? props.$tileWidth
        : TILE_WIDTH_PX_SM}px;
    width: ${props =>
      props.$isLargeTiles && props.$tileWidth
        ? props.$tileWidth
        : TILE_WIDTH_PX_SM}px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 16px;
    opacity: 0.6;
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeModalBackgroundColor : lightText};
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : darkGrey)};
  }

  .tile-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .info-icon {
    font-size: ${props =>
      responsiveFontSize(props.$isLargeTiles, props.$tileWidth, 2)};
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : darkGrey)};
    margin-bottom: 6px;
  }

  .message-title {
    font-size: ${props =>
      responsiveFontSize(props.$isLargeTiles, props.$tileWidth, 1)};
    font-weight: 500;
    margin-bottom: 4px;
    line-height: 1.2;
  }

  .message-body {
    font-size: ${props =>
      responsiveFontSize(props.$isLargeTiles, props.$tileWidth, 0.7)};
    line-height: 1.2;
    max-width: 100%;
    margin: 0 auto;
  }
`;
